import FormulaError from './FormulaError.js';
import { tokenize, NUMBER, REF, IDENT, OP, LPAREN, RPAREN, COMMA } from './tokenizer.js';

// Allowed functions and their argument count limits
export const FUNCTIONS = {
  IF: { min: 3, max: 3 },
  AND: { min: 1, max: Infinity },
  OR: { min: 1, max: Infinity },
  NOT: { min: 1, max: 1 },
  ABS: { min: 1, max: 1 },
  MIN: { min: 1, max: Infinity },
  MAX: { min: 1, max: Infinity },
  ROUND: { min: 1, max: 2 },
};

const COMPARISON_OPS = ['<', '<=', '>', '>=', '=', '<>'];
const ADDITIVE_OPS = ['+', '-'];
const MULTIPLICATIVE_OPS = ['*', '/'];

/*
  Recursive-descent parser producing an AST:

    expression     := comparison
    comparison     := additive (COMPARISON_OP additive)?
    additive       := multiplicative (('+' | '-') multiplicative)*
    multiplicative := unary (('*' | '/') unary)*
    unary          := '-' unary | primary
    primary        := NUMBER | REF | FUNCTION '(' args ')' | '(' expression ')'

  Node shapes:
    { type: 'number', value }
    { type: 'ref', accessor }
    { type: 'unary', op, arg }
    { type: 'binary', op, left, right }
    { type: 'call', name, args }
*/
export const parse = (formula) => {
  const tokens = tokenize(formula ?? '');
  let index = 0;

  const peek = () => tokens[index];
  const endPos = () => (formula ?? '').length;

  const next = () => {
    const token = tokens[index];
    if (!token) {
      throw new FormulaError('formula_error_unexpected_end', endPos());
    }
    index += 1;
    return token;
  };

  const isOp = (ops) => {
    const token = peek();
    return token?.type === OP && ops.includes(token.value);
  };

  const expect = (type) => {
    const token = peek();
    if (token?.type === type) return next();

    if (!token) {
      const key = type === RPAREN ? 'formula_error_unclosed_parenthesis' : 'formula_error_unexpected_end';
      throw new FormulaError(key, endPos());
    }
    throw new FormulaError('formula_error_unexpected_token', token.pos, { token: tokenText(token) });
  };

  const parseExpression = () => parseComparison();

  const parseComparison = () => {
    const left = parseAdditive();
    if (isOp(COMPARISON_OPS)) {
      const op = next().value;
      return { type: 'binary', op, left, right: parseAdditive() };
    }
    return left;
  };

  const parseAdditive = () => {
    let node = parseMultiplicative();
    while (isOp(ADDITIVE_OPS)) {
      const op = next().value;
      node = { type: 'binary', op, left: node, right: parseMultiplicative() };
    }
    return node;
  };

  const parseMultiplicative = () => {
    let node = parseUnary();
    while (isOp(MULTIPLICATIVE_OPS)) {
      const op = next().value;
      node = { type: 'binary', op, left: node, right: parseUnary() };
    }
    return node;
  };

  const parseUnary = () => {
    if (isOp(['-', '+'])) {
      const op = next().value;
      const arg = parseUnary();
      return op === '-' ? { type: 'unary', op, arg } : arg;
    }
    return parsePrimary();
  };

  const parseCall = (token) => {
    // A bare word that isn't a function call, e.g. "pears" typed instead of picking a column
    if (peek()?.type !== LPAREN) {
      throw new FormulaError('formula_error_unknown_name', token.pos, { name: token.value });
    }

    const spec = FUNCTIONS[token.value];
    if (!spec) {
      throw new FormulaError('formula_error_unknown_function', token.pos, { name: token.value });
    }

    expect(LPAREN);
    const args = [];
    if (peek()?.type !== RPAREN) {
      args.push(parseExpression());
      while (peek()?.type === COMMA) {
        next();
        args.push(parseExpression());
      }
    }
    expect(RPAREN);

    if (args.length < spec.min || args.length > spec.max) {
      throw new FormulaError('formula_error_argument_count', token.pos, { name: token.value });
    }
    return { type: 'call', name: token.value, args };
  };

  const parsePrimary = () => {
    const token = next();

    switch (token.type) {
      case NUMBER:
        return { type: 'number', value: token.value };
      case REF:
        return { type: 'ref', accessor: token.value };
      case IDENT:
        return parseCall(token);
      case LPAREN: {
        const node = parseExpression();
        expect(RPAREN);
        return node;
      }
      default:
        throw new FormulaError('formula_error_unexpected_token', token.pos, { token: tokenText(token) });
    }
  };

  if (!tokens.length) {
    throw new FormulaError('formula_error_empty', 0);
  }

  const ast = parseExpression();
  const leftover = peek();
  if (leftover) {
    throw new FormulaError('formula_error_unexpected_token', leftover.pos, { token: tokenText(leftover) });
  }
  return ast;
};

const tokenText = (token) => {
  switch (token.type) {
    case REF:
      return `[${token.value}]`;
    case NUMBER:
    case IDENT:
    case OP:
      return String(token.value);
    default:
      return token.type;
  }
};

// Collects every column accessor referenced by the AST
export const collectReferences = (node, refs = new Set()) => {
  switch (node.type) {
    case 'ref':
      refs.add(node.accessor);
      break;
    case 'unary':
      collectReferences(node.arg, refs);
      break;
    case 'binary':
      collectReferences(node.left, refs);
      collectReferences(node.right, refs);
      break;
    case 'call':
      node.args.forEach(arg => collectReferences(arg, refs));
      break;
  }
  return refs;
};
